import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { appointmentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Calendar, 
  Clock, 
  Plus,
  ArrowLeft,
  X,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function CustomerAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelId, setCancelId] = useState(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await appointmentsAPI.getAll();
      setAppointments(response.data);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error('Fehler beim Laden der Termine');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelId) return;
    
    try {
      await appointmentsAPI.cancel(cancelId);
      toast.success('Termin erfolgreich storniert');
      fetchAppointments();
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      toast.error('Fehler beim Stornieren des Termins');
    } finally {
      setCancelId(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
      completed: 'bg-blue-100 text-blue-700',
    };
    const labels = {
      confirmed: 'Bestätigt',
      cancelled: 'Storniert',
      completed: 'Abgeschlossen',
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{labels[status] || status}</Badge>;
  };

  const upcomingAppointments = appointments
    .filter(apt => apt.status !== 'cancelled' && new Date(apt.date) >= new Date())
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const pastAppointments = appointments
    .filter(apt => apt.status === 'cancelled' || new Date(apt.date) < new Date())
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-4 md:py-8" data-testid="customer-appointments">
      <div className="max-w-4xl mx-auto px-4 md:px-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 md:mb-8">
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="px-2 md:px-4">
                <ArrowLeft className="w-4 h-4 md:mr-2" />
                <span className="hidden md:inline">Zurück</span>
              </Button>
            </Link>
            <h1 className="font-barlow font-bold text-xl md:text-2xl text-[#1A1A1A]">
              Meine Termine
            </h1>
          </div>
          <Link to="/termin" className="w-full sm:w-auto">
            <Button className="btn-primary w-full sm:w-auto" data-testid="new-appointment-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neuer Termin
            </Button>
          </Link>
        </div>

        {/* Upcoming Appointments */}
        <Card className="mb-8" data-testid="upcoming-appointments-list">
          <CardHeader>
            <CardTitle className="font-barlow text-xl flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#9B1B30]" />
              Kommende Termine ({upcomingAppointments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingAppointments.length > 0 ? (
              <div className="space-y-4">
                {upcomingAppointments.map((apt) => (
                  <div 
                    key={apt.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg gap-4"
                    data-testid={`appointment-${apt.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-[#9B1B30] text-white rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                        <span className="font-barlow font-bold text-xl leading-none">
                          {format(new Date(apt.date), 'd')}
                        </span>
                        <span className="font-manrope text-xs">
                          {format(new Date(apt.date), 'MMM', { locale: de })}
                        </span>
                      </div>
                      <div>
                        <p className="font-manrope font-medium">
                          {format(new Date(apt.date), 'EEEE, d. MMMM yyyy', { locale: de })}
                        </p>
                        <p className="font-manrope text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {apt.time} Uhr · {apt.duration} Minuten
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(apt.status)}
                      {apt.status === 'confirmed' && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setCancelId(apt.id)}
                          data-testid={`cancel-btn-${apt.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Stornieren
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="font-manrope text-gray-500 mb-4">Keine kommenden Termine</p>
                <Link to="/termin">
                  <Button className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" /> Jetzt Termin buchen
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Past Appointments */}
        {pastAppointments.length > 0 && (
          <Card data-testid="past-appointments-list">
            <CardHeader>
              <CardTitle className="font-barlow text-xl flex items-center gap-2 text-gray-500">
                <Clock className="w-5 h-5" />
                Vergangene Termine ({pastAppointments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {pastAppointments.map((apt) => (
                  <div 
                    key={apt.id} 
                    className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50/50 rounded-lg gap-4 opacity-70"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gray-300 text-white rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                        <span className="font-barlow font-bold text-xl leading-none">
                          {format(new Date(apt.date), 'd')}
                        </span>
                        <span className="font-manrope text-xs">
                          {format(new Date(apt.date), 'MMM', { locale: de })}
                        </span>
                      </div>
                      <div>
                        <p className="font-manrope font-medium">
                          {format(new Date(apt.date), 'EEEE, d. MMMM yyyy', { locale: de })}
                        </p>
                        <p className="font-manrope text-sm text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {apt.time} Uhr · {apt.duration} Minuten
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(apt.status)}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Cancel Dialog */}
      <AlertDialog open={!!cancelId} onOpenChange={() => setCancelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-barlow flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Termin stornieren?
            </AlertDialogTitle>
            <AlertDialogDescription className="font-manrope">
              Möchten Sie diesen Termin wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-manrope">Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancel}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-btn"
            >
              Termin stornieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
